import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { loadOpenRunId, saveOpenRunId } from "../lib/storage";
import type { RunStatus } from "../lib/types";

/** The run routes, and the only place a run id may come from in the URL. */
const RUN_ROUTE = /^\/runs\/([^/]+)\/(editor|review|studio)/;

export type OpenRun = {
  /** The run the operator has open. NULL means no run is open, and the nav must say so. */
  run: RunStatus | null;
  /** True while the URL itself names a run — the run screens. */
  onRunRoute: boolean;
  /** The id in the URL, whether or not it resolves to a run we hold. */
  routeRunId: string;
};

/**
 * WHICH RUN IS OPEN — and never, ever a guess.
 *
 * THE BUG THIS DELETES. AppShell derived the Editor and Review links from `runs[0]`:
 *
 *     const latestRunId = runs[0]?.runId || "local";
 *     const editorPath = `/runs/${latestRunId}/editor`;
 *
 * So "Editor" opened the most recent run — silently. It never named the run, never said one
 * had been chosen, and if you had two runs it opened the wrong one without a word. Both
 * screens then compounded it: `runs.find(id) || runs[0]` meant even a WRONG id in the URL
 * quietly rendered a different run's document. That is why the tabs felt like they pointed
 * at something unexplained. They did.
 *
 * A run is open only if the operator OPENED it:
 *   - the URL names it (they navigated to it, deep-linked it, or just created it), or
 *   - they opened it earlier and we remembered the id.
 * Otherwise no run is open. There is no fallback to "the newest one", because "the newest
 * one" is a guess the operator never made, and the chrome would have to lie to present it.
 *
 * Whatever this returns, the chrome NAMES it. That is the other half of the contract: a run
 * may be remembered, but it may never be anonymous.
 */
export function useOpenRun(runs: RunStatus[]): OpenRun {
  const { pathname } = useLocation();
  const routeRunId = RUN_ROUTE.exec(pathname)?.[1] || "";
  const [rememberedId, setRememberedId] = useState(loadOpenRunId);

  /* Landing on a run's own URL IS opening it. Remember it, so the "This run" group survives
     a trip to Settings and back — named, not inferred. */
  useEffect(() => {
    if (!routeRunId || routeRunId === rememberedId) return;
    if (!runs.some((run) => run.runId === routeRunId)) return;
    setRememberedId(routeRunId);
    saveOpenRunId(routeRunId);
  }, [routeRunId, rememberedId, runs]);

  /* ON A RUN ROUTE THE URL IS THE AUTHORITY. If it names a run we do not hold, then no run
     is open — we do not fall back to the remembered one, because the screen the operator is
     looking at is not showing that run either. The chrome and the page tell one story. */
  const run = routeRunId
    ? runs.find((item) => item.runId === routeRunId) || null
    : runs.find((item) => item.runId === rememberedId) || null;

  return { run, onRunRoute: Boolean(routeRunId), routeRunId };
}
